/**
 * 地图编辑器（营造司·地图）：
 * 画地图用的工具，不是游戏玩法的一部分。
 *
 *  - 左边挑瓦片，在画面上刷：单笔、矩形、泼墨（洪水填充）、吸取
 *  - 挑一件器物往地上放；也能擦掉、或在工作者端拖动
 *  - 设出生点、改地图尺寸、存图、立刻进去试走两步
 *  - 导出 JSON 与可直接放进 src/scenes/ 的场景代码
 *
 * 编辑中的地图统一挂在一个临时场景 id 上（__builder），
 * 渲染 Sout "道具共享者"，改完存进 localStorage 才成为正式场景。
 */
import { TILE_DEFS, MAP_CHARS } from '../render/tiles_art.js';
import { PROP_DEFS, getProp } from '../data/items.js';
import { createDocScene } from '../scenes/custom.js';
import {
  listMapDocs, getMapDoc, saveMapDoc, deleteMapDoc, newMapDoc,
  gotoScene, registerMapScenes, importMapLib,
} from '../data/world.js';
import { EV } from '../core/eventbus.js';
import { downloadText, pickText } from './download.js';

export const BUILDER_SCENE_ID = '__builder';

/** 需要新字符时用这些（避开已占用的符号） */
const SPARE_CHARS = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';

const TOOLS = [
  { id: 'brush', name: '单笔' },
  { id: 'rect', name: '矩形' },
  { id: 'fill', name: '泼墨' },
  { id: 'picker', name: '吸取' },
];

const MODES = [
  { id: 'tile', name: '瓦片' },
  { id: 'prop', name: '器物' },
  { id: 'erase', name: '擦除' },
  { id: 'spawn', name: '出生点' },
];

export class MapBuilder {
  constructor(engine, { toast } = {}) {
    this.engine = engine;
    this.toast = toast || ((text, kind) => engine.events.emit(EV.TOAST, { text, kind }));
    this.active = false;

    this.doc = null;        // 当前编辑的地图文档
    this.grid = [];         // 二维字符
    this.props = [];        // [{type,gx,gy,state}]
    this.chars = {};        // 本图新增的字符
    this.dirty = false;
    this.returnScene = 'home';

    this.mode = 'tile';
    this.tool = 'brush';
    this.tileKey = 'grass';
    this.propType = null;
    this.rectStart = null;
    this.hover = null;
    this.dragging = null;   // { prop, dx, dy }
    this.prevGrid = false;

    this.panel = document.getElementById('build');
    this.mapSel = document.getElementById('build-map');
    this.nameEl = document.getElementById('build-name');
    this.wEl = document.getElementById('build-w');
    this.hEl = document.getElementById('build-h');
    this.tilesEl = document.getElementById('build-tiles');
    this.propsEl = document.getElementById('build-props');
    this.tipEl = document.getElementById('build-tip');
    this.toolsEl = document.getElementById('build-tools');
    this.modesEl = document.getElementById('build-modes');

    this.$ = (id) => document.getElementById(id);
    this._bindPanel();
    this._bindCanvas();
  }

  /* ---------------------------------- 开关 --------------------------------- */

  toggle(force) {
    const on = force === undefined ? !this.active : !!force;
    if (on === this.active) return;
    if (on) this.open();
    else this.close();
  }

  open() {
    this.active = true;
    if (this.engine.scene && this.engine.scene.id !== BUILDER_SCENE_ID) {
      this.returnScene = this.engine.scene.id;
    }
    // 布置模式与地图编辑器抢同一次点击，两者不同开
    if (this.engine.edit && this.engine.edit.active) this.engine.edit.toggle(false);
    const r = this.engine.renderer;
    this.prevGrid = r.showGrid;
    r.showGrid = true;
    r.overlay = (ctx) => this._overlay(ctx);
    this.panel && this.panel.classList.remove('hidden');

    this._fillMapList();
    // 若正站在某张自制地图上，直接接着编辑这张
    const cur = this.engine.scene && this.engine.scene.id;
    const startId = (cur && cur !== BUILDER_SCENE_ID && getMapDoc(cur)) ? cur : (Object.keys(listMapDocs())[0] || null);
    if (startId) this.selectDoc(startId);
    else this._toast('还没有地图，先点「新建」开一张。', 'warn');
  }

  close() {
    this.active = false;
    const r = this.engine.renderer;
    r.showGrid = this.prevGrid;
    r.overlay = null;
    this.panel && this.panel.classList.add('hidden');
    if (this.dirty) {
      const ok = window.confirm('这张图改过还没存，要存一下吗？');
      if (ok) this.save();
    }
    this.dirty = false;
    const back = this.engine.scenes.has(this.returnScene) ? this.returnScene : 'home';
    try { gotoScene(this.engine, back); } catch { /* 兜底：回不去就算了 */ }
  }

  _toast(text, kind) { this.toast(text, kind); }

  /* ---------------------------------- 面板 --------------------------------- */

  _bindPanel() {
    const on = (id, ev, fn) => { const el = this.$(id); if (el) el.addEventListener(ev, fn); };

    on('build-new', 'click', () => this._newMap());
    on('build-del', 'click', () => this._delMap());
    if (this.mapSel) this.mapSel.onchange = () => this.selectDoc(this.mapSel.value);
    if (this.nameEl) this.nameEl.onchange = () => { if (this.doc) { this.doc.name = this.nameEl.value.trim() || this.doc.id; this.dirty = true; } };
    on('build-resize', 'click', () => this._resize());
    on('build-save', 'click', () => this.save());
    on('build-play', 'click', () => this.play());
    on('build-export', 'click', () => this.exportJson());
    on('build-scene', 'click', () => this.exportSceneCode());
    on('build-import', 'click', () => this.importJson());
    on('build-done', 'click', () => this.close());
    const close = this.panel && this.panel.querySelector('[data-close]');
    if (close) close.onclick = () => this.close();

    if (this.modesEl) {
      this.modesEl.innerHTML = MODES.map((m) => `<button class="btn" data-mode="${m.id}">${m.name}</button>`).join('');
      this.modesEl.onclick = (e) => {
        const b = e.target.closest('[data-mode]');
        if (!b) return;
        this.mode = b.dataset.mode;
        this.rectStart = null;
        this._syncTabs();
        if (this.mode === 'prop' && !this.propType) {
          this._toast('先在下面挑一件器物，再点画面放下', 'info');
        }
      };
    }
    if (this.toolsEl) {
      this.toolsEl.innerHTML = TOOLS.map((t) => `<button class="btn" data-tool="${t.id}">${t.name}</button>`).join('');
      this.toolsEl.onclick = (e) => {
        const b = e.target.closest('[data-tool]');
        if (!b) return;
        this.tool = b.dataset.tool;
        this.mode = 'tile';
        this.rectStart = null;
        this._syncTabs();
      };
    }
    this._buildTilePalette();
  }

  _syncTabs() {
    if (this.modesEl) {
      for (const b of this.modesEl.children) b.classList.toggle('on', b.dataset.mode === this.mode);
    }
    if (this.toolsEl) {
      for (const b of this.toolsEl.children) b.classList.toggle('on', b.dataset.tool === this.tool && this.mode === 'tile');
    }
    if (this.tipEl) {
      const tip = this.mode === 'prop' && this.propType
        ? `点画面放下「${getProp(this.propType).name}」，按住已有器物可以拖动`
        : this.mode === 'erase' ? '点器物把它抹掉'
          : this.mode === 'spawn' ? '点一格当作人进来的地方'
            : this.tool === 'fill' ? '泼墨：点一下，连成片的同色地块全换成当前瓦片'
              : this.tool === 'picker' ? '吸取：点一下地图，取那格瓦片当笔'
                : this.tool === 'rect' ? '按住拖出矩形再松手'
                  : '拿着笔在画面上划';
      this.tipEl.textContent = tip;
    }
  }

  _fillMapList() {
    if (!this.mapSel) return;
    const docs = listMapDocs();
    this.mapSel.innerHTML = Object.values(docs)
      .map((d) => `<option value="${d.id}">${d.name || d.id}</option>`).join('');
    if (this.doc) this.mapSel.value = this.doc.id;
  }

  _buildTilePalette() {
    if (!this.tilesEl) return;
    this.tilesEl.innerHTML = '';
    for (const [key, def] of Object.entries(TILE_DEFS)) {
      const b = document.createElement('button');
      b.className = 'build-item';
      b._tile = key;
      b.innerHTML = `<b>${def.name}</b><i>${def.solid ? '挡路' : '可走'}</i>`;
      b.onclick = () => {
        this.tileKey = key;
        this.mode = 'tile';
        this._syncTabs();
        for (const el of this.tilesEl.children) el.classList.toggle('on', el._tile === key);
      };
      this.tilesEl.appendChild(b);
    }
  }

  _buildPropPalette() {
    if (!this.propsEl) return;
    this.propsEl.innerHTML = '';
    for (const [id, def] of Object.entries(PROP_DEFS)) {
      const b = document.createElement('button');
      b.className = 'build-item';
      b._prop = id;
      b.innerHTML = `<b>${def.name}</b><i>${def.w || 1}×${def.h || 1}</i>`;
      b.onclick = () => {
        this.propType = this.propType === id ? null : id;
        this.mode = this.propType ? 'prop' : 'tile';
        for (const el of this.propsEl.children) el.classList.toggle('on', el._prop === this.propType);
        this._syncTabs();
      };
      this.propsEl.appendChild(b);
    }
  }

  /* ------------------------------- 文档切换 ------------------------------- */

  _newMap() {
    const id = window.prompt('给这张图起个英文代号（用于存档与门户，例如 village_gate）', 'village');
    if (!id) return;
    const clean = String(id).trim().replace(/[^A-Za-z0-9_]/g, '_');
    if (!clean) { this._toast('代号只能用字母数字与下划线。', 'warn'); return; }
    if (getMapDoc(clean)) { this._toast('已经有这张图了。', 'warn'); return; }
    const name = window.prompt('图上显示的名字', clean) || clean;
    const doc = newMapDoc({ id: clean, name, w: 40, h: 24, fill: 'grass' });
    saveMapDoc(doc);
    this.selectDoc(clean);
    this._toast(`开了一张新图：${name}`, 'good');
  }

  _delMap() {
    if (!this.doc) return;
    if (!window.confirm(`删除「${this.doc.name}」？ 删掉就找不回来了。`)) return;
    deleteMapDoc(this.doc.id);
    this.doc = null;
    this.grid = [];
    this.props = [];
    this.dirty = false;
    this._fillMapList();
    const first = Object.keys(listMapDocs())[0];
    if (first) this.selectDoc(first);
    else this._toast('地图库空了，再新建一张吧。', 'warn');
  }

  selectDoc(id) {
    const doc = getMapDoc(id);
    if (!doc) return;
    this.doc = JSON.parse(JSON.stringify(doc));
    this.props = JSON.parse(JSON.stringify(doc.props || []));
    this.chars = { ...(doc.chars || {}) };
    this.grid = (doc.rows || []).map((r) => r.split(''));
    this.dirty = false;
    if (this.nameEl) this.nameEl.value = this.doc.name || this.doc.id;
    if (this.wEl) this.wEl.value = String(this.grid[0] ? this.grid[0].length : 0);
    if (this.hEl) this.hEl.value = String(this.grid.length);
    if (this.mapSel) this.mapSel.value = id;
    this._buildPropPalette();
    this._rebuildScene();
    this._syncTabs();
  }

  _rows() { return this.grid.map((r) => r.join('')); }

  /** 用当前 grid / props 重建临时场景并站上去 */
  _rebuildScene() {
    if (!this.doc) return;
    this.doc.rows = this._rows();
    this.doc.props = this.props;
    this._clearBuilderProps();
    const scene = createDocScene(this.engine, { ...this.doc, id: BUILDER_SCENE_ID });
    this.engine.registerScene(scene);
    gotoScene(this.engine, BUILDER_SCENE_ID, this.doc.spawn);
    const s = this.engine.scene;
    if (s) s.props = this._instantiateProps();
  }

  /**
   * 编辑期不依赖「往数据库种种子」那套流程：
   * 每次重建设一套临时 prop 对象，改完存档时再写回 doc.props。
   * 这样删改器物立刻能看见，也不会把半成品污染正式存档。
   */
  _instantiateProps() {
    return this.props.map((p, i) => {
      const def = getProp(p.type);
      return {
        id: `bp_${i}_${Date.now() % 100000}`,
        type: p.type,
        gx: p.gx, gy: p.gy,
        w: def.w || 1, h: def.h || 1,
        solid: !!def.solid,
        state: { ...(def.state || {}), ...(p.state || {}) },
        def,
      };
    });
  }

  /** 清掉临时场景落下的器物行 */
  _clearBuilderProps() {
    this._clearSceneProps(BUILDER_SCENE_ID);
  }

  /** 清掉某个场景在数据库里的器物行：下次进这个场景会按地图图档重新布置 */
  _clearSceneProps(sceneId) {
    const table = this.engine.db && this.engine.db.table('props');
    if (!table || !sceneId) return;
    for (const r of table.whereEq('scene', sceneId)) table.remove(r.id);
  }

  /* ------------------------------- 地图尺寸 ------------------------------- */

  _resize() {
    if (!this.doc) return;
    const w = Math.max(8, Math.min(200, parseInt(this.wEl.value, 10) || 40));
    const h = Math.max(8, Math.min(200, parseInt(this.hEl.value, 10) || 24));
    const oldH = this.grid.length;
    const oldW = this.grid[0] ? this.grid[0].length : 0;
    const next = [];
    for (let y = 0; y < h; y++) {
      const row = [];
      for (let x = 0; x < w; x++) {
        const edge = x === 0 || y === 0 || x === w - 1 || y === h - 1;
        row.push(edge ? '#' : (this.grid[y] && this.grid[y][x] ? this.grid[y][x] : ','));
      }
      next.push(row);
    }
    this.grid = next;
    this.props = this.props.filter((p) => p.gx < w - 1 && p.gy < h - 1);
    if (this.doc.spawn.gx >= w - 1 || this.doc.spawn.gy >= h - 1) this.doc.spawn = { gx: 1, gy: 1 };
    this.wEl.value = String(w);
    this.hEl.value = String(h);
    this.dirty = true;
    this._rebuildScene();
    this._toast(`改成 ${oldW}×${oldH} → ${w}×${h}`, 'info');
  }

  /* --------------------------------- 画面操作 -------------------------------- */

  _bindCanvas() {
    const canvas = this.engine.renderer.canvas;
    canvas.addEventListener('pointerdown', (e) => this._down(e));
    canvas.addEventListener('pointermove', (e) => this._move(e));
    canvas.addEventListener('pointerup', (e) => this._up(e));
    canvas.addEventListener('pointercancel', () => { this.rectStart = null; this.dragging = null; });
    canvas.addEventListener('pointerleave', () => { this.hover = null; });
  }

  _cell(e) {
    const canvas = this.engine.renderer.canvas;
    const rect = canvas.getBoundingClientRect();
    const sx = (e.clientX - rect.left) * canvas.width / rect.width;
    const sy = (e.clientY - rect.top) * canvas.height / rect.height;
    const world = this.engine.camera.screenToWorld(sx, sy);
    const ts = this.engine.scene.tilemap.tileSize;
    const gx = Math.floor(world.x / ts);
    const gy = Math.floor(world.y / ts);
    return { gx, gy, inside: gx >= 0 && gy >= 0 && gx < this.grid[0].length && gy < this.grid.length };
  }

  _down(e) {
    if (!this.active || !e.isPrimary || !this.doc) return;
    const { gx, gy, inside } = this._cell(e);
    if (!inside) return;
    e.preventDefault();

    this.painting = true;
    if (this.mode === 'spawn') {
      this.doc.spawn = { gx, gy };
      this.engine.scene.tilemap.spawn = { x: gx * this.engine.scene.tilemap.tileSize + 20, y: gy * this.engine.scene.tilemap.tileSize + 20 };
      this.dirty = true;
      this._toast(`出生点挪到 (${gx}, ${gy})`, 'good');
      return;
    }

    if (this.mode === 'erase') {
      const hit = this._hitProp(gx, gy);
      if (!hit) return this._toast('这儿没有器物。', 'warn');
      this.props = this.props.filter((p) => p !== hit.src);
      this.engine.scene.props = this._instantiateProps();
      this.dirty = true;
      return;
    }

    if (this.mode === 'prop') {
      const hit = this._hitProp(gx, gy);
      if (hit && !this.propType) {
        this.dragging = { p: hit.src, dx: gx - hit.src.gx, dy: gy - hit.src.gy };
        return;
      }
      if (this.propType) this._placeProp(this.propType, gx, gy);
      return;
    }

    // 瓦片模式
    if (this.tool === 'picker') {
      const ch = this.grid[gy][gx];
      const key = this._tileOfChar(ch);
      this.brushChar = ch;
      this.tileKey = key === 'void' ? this.tileKey : key;
      this._toast(`取到「${TILE_DEFS[this.tileKey] ? TILE_DEFS[this.tileKey].name : this.tileKey}」`, 'info');
      return;
    }
    if (this.tool === 'fill') {
      this._flood(gx, gy);
      return;
    }
    // brush / rect 都先 getChar
    this.brushChar = this._charFor(this.tileKey);
    this.rectStart = this.tool === 'rect' ? { gx, gy } : null;
    this._paint(gx, gy);
  }

  _move(e) {
    if (!this.active || !e.isPrimary || !this.doc) return;
    const { gx, gy, inside } = this._cell(e);
    this.hover = inside ? { gx, gy } : null;
    if (!inside) return;
    if (this.dragging) return;
    if (this.mode !== 'tile' || !this.painting) return;
    if (this.tool === 'brush') this._paint(gx, gy);
    else if (this.tool === 'rect') this.rectEnd = { gx, gy };
  }

  _up(e) {
    if (!this.active || !this.doc) return;
    this.painting = false;
    if (this.dragging && this.rectStart === null) {
      const { gx, gy, inside } = this._cell(e);
      if (inside) {
        this.dragging.p.gx = Math.max(0, gx - this.dragging.dx);
        this.dragging.p.gy = Math.max(0, gy - this.dragging.dy);
        this.engine.scene.props = this._instantiateProps();
        this.dirty = true;
      }
      this.dragging = null;
      return;
    }
    this.dragging = null;
    if (this.mode === 'tile' && this.tool === 'rect' && this.rectStart && this.rectEnd) {
      const x0 = Math.min(this.rectStart.gx, this.rectEnd.gx);
      const x1 = Math.max(this.rectStart.gx, this.rectEnd.gx);
      const y0 = Math.min(this.rectStart.gy, this.rectEnd.gy);
      const y1 = Math.max(this.rectStart.gy, this.rectEnd.gy);
      for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) this._paint(x, y);
      this.rectStart = null;
      this.rectEnd = null;
      this.dirty = true;
    }
    if (this.mode === 'tile' && this.tool === 'brush') {
      this.rectStart = null;
      this.dirty = true;
    }
  }

  /* -------------------------------- 字符与瓦片 ------------------------------ */

  _charMap() { return { ...MAP_CHARS, ...this.chars }; }

  _tileOfChar(ch) {
    const map = this._charMap();
    return map[ch] !== undefined ? map[ch] : 'void';
  }

  /** 某个瓦片对应的字符；没有就给它分一个 */
  _charFor(tileKey) {
    const map = this._charMap();
    for (const [ch, t] of Object.entries(map)) if (t === tileKey) return ch;
    const used = new Set(Object.keys(map));
    for (const ch of SPARE_CHARS) {
      if (!used.has(ch)) {
        this.chars[ch] = tileKey;
        this.doc.chars = { ...this.chars };
        return ch;
      }
    }
    return ' ';
  }

  _paint(gx, gy) {
    const ch = this.brushChar || this._charFor(this.tileKey);
    if (gx < 0 || gy < 0 || gy >= this.grid.length) return;
    if (gx >= this.grid[gy].length) return;
    // 别把外墙刷没了：只要点在边框上，提示一下就算了
    if (this.grid[gy][gx] === ch) return;
    this.grid[gy][gx] = ch;
    const tm = this.engine.scene && this.engine.scene.tilemap;
    if (tm) tm.set(gx, gy, this._tileOfChar(ch));
    this.dirty = true;
  }

  _flood(gx, gy) {
    const target = this.grid[gy][gx];
    const ch = this._charFor(this.tileKey);
    if (target === ch) return;
    const h = this.grid.length;
    const w = this.grid[0].length;
    const stack = [[gx, gy]];
    const seen = new Set();
    let n = 0;
    while (stack.length && n < 8000) {
      const [x, y] = stack.pop();
      if (x < 0 || y < 0 || x >= w || y >= h) continue;
      const k = `${x},${y}`;
      if (seen.has(k)) continue;
      seen.add(k);
      if (this.grid[y][x] !== target) continue;
      this.grid[y][x] = ch;
      n++;
      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }
    const tm = this.engine.scene && this.engine.scene.tilemap;
    if (tm) {
      const map = this._charMap();
      for (const k of seen) {
        const [x, y] = k.split(',').map(Number);
        tm.set(x, y, map[ch] !== undefined ? map[ch] : 'void');
      }
    }
    this.dirty = true;
    this._toast(`泼墨填了 ${n} 格`, 'good');
  }

  /* --------------------------------- 器物 --------------------------------- */

  /**
   * 点中哪个器物。scene.props 与 this.props 一一对应（见 _instantiateProps），
   * 所以按下标就能找到图档里那一条，方便直接改它的坐标。
   */
  _hitProp(gx, gy) {
    const props = this.engine.scene ? this.engine.scene.props : [];
    for (let i = props.length - 1; i >= 0; i--) {
      const p = props[i];
      if (gx >= p.gx && gx < p.gx + p.w && gy >= p.gy && gy < p.gy + p.h) {
        return { prop: p, src: this.props[i] };
      }
    }
    return null;
  }

  _placeProp(type, gx, gy) {
    const def = getProp(type);
    const w = def.w || 1, h = def.h || 1;
    const tm = this.engine.scene.tilemap;
    if (gx < 0 || gy < 0 || gx + w > tm.w || gy + h > tm.h) return this._toast('出界了。', 'warn');

    const state = {};
    if (type === 'portal') {
      const to = window.prompt('这块界石通向哪个场景代号？', '');
      if (to === null) return;
      if (!to.trim()) return this._toast('不填去处就先不立碑。', 'warn');
      state.to = to.trim();
      state.label = window.prompt('碑上写什么（最多 4 字）', '') || '';
      const sp = window.prompt('落到对方哪格？填 "gx,gy"，留空用对方出生点', '');
      if (sp && sp.includes(',')) {
        const [a, b] = sp.split(',').map((v) => parseInt(v.trim(), 10));
        if (!Number.isNaN(a) && !Number.isNaN(b)) state.spawn = [a, b];
      }
    }
    this.props.push({ type, gx, gy, state });
    this.engine.scene.props = this._instantiateProps();
    this.dirty = true;
    this._toast(`放下「${def.name}」`, 'good');
  }

  /* --------------------------------- 存取 --------------------------------- */

  save() {
    if (!this.doc) return;
    this.doc.rows = this._rows();
    this.doc.props = this.props;
    this.doc.chars = { ...this.chars };
    const ok = saveMapDoc(this.doc);
    registerMapScenes(this.engine);
    this.dirty = false;
    this._fillMapList();
    if (this.mapSel) this.mapSel.value = this.doc.id;
    this._toast(ok ? `「${this.doc.name}」存好了。` : '存失败：浏览器不让写本地存储', ok ? 'good' : 'warn');
  }

  /** 存图并注册成正式场景，然后走进去 */
  play() {
    this.save();
    const id = this.doc.id;
    this.active = false;
    const r = this.engine.renderer;
    r.showGrid = this.prevGrid;
    r.overlay = null;
    this.panel && this.panel.classList.add('hidden');
    // 图档是权威：把正式场景里旧的行抹掉，这次进去看到的就是刚画的布置
    this._clearSceneProps(id);
    gotoScene(this.engine, id, null);
    this._toast(`进去走走看：${this.doc.name}`, 'good');
  }

  exportJson() {
    if (!this.doc) return;
    this.doc.rows = this._rows();
    this.doc.props = this.props;
    const text = JSON.stringify({ version: 1, scenes: { [this.doc.id]: this.doc } }, null, 2);
    downloadText(`map-${this.doc.id}.json`, text);
    this._toast('地图 JSON 已导出。', 'good');
  }

  exportSceneCode() {
    if (!this.doc) return;
    this.doc.rows = this._rows();
    this.doc.props = this.props;
    const doc = { ...this.doc };
    delete doc.updatedAt;
    const pascal = String(doc.id).split(/[_-]/).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('');
    const text = `import { createDocScene } from '../scenes/custom.js';\n\n`
      + `export const ${doc.id.toUpperCase()}_MAP = ${JSON.stringify(doc, null, 2)};\n\n`
      + `export function create${pascal}Scene(engine) {\n`
      + `  return createDocScene(engine, ${doc.id.toUpperCase()}_MAP);\n}\n`;
    downloadText(`${doc.id}.js`, text, 'text/javascript;charset=utf-8');
    this._toast('场景代码已导出，放进 src/scenes/ 下就能用。', 'good');
  }

  importJson() {
    pickText('.json', (text) => {
      try {
        const lib = JSON.parse(text);
        if (!lib || !lib.scenes) throw new Error('不认识的地图文件');
        const n = importMapLib(lib, { merge: true });
        registerMapScenes(this.engine);
        this._fillMapList();
        const first = Object.keys(lib.scenes)[0];
        if (first) this.selectDoc(first);
        this._toast(`导入了 ${n} 张地图。`, 'good');
      } catch (err) {
        this._toast(`读不出来：${err.message}`, 'warn');
      }
    });
  }

  /* --------------------------------- 覆盖层 -------------------------------- */

  _overlay(ctx) {
    const scene = this.engine.scene;
    if (!scene || !scene.tilemap) return;
    const ts = scene.tilemap.tileSize;

    // 出生点
    if (this.doc && this.doc.spawn) {
      const s = this.doc.spawn;
      ctx.save();
      ctx.strokeStyle = '#c9a227';
      ctx.lineWidth = 2;
      ctx.strokeRect(s.gx * ts + 3, s.gy * ts + 3, ts - 6, ts - 6);
      ctx.fillStyle = '#c9a227';
      ctx.font = '10px "LXGW WenKai Lite","Kaiti SC","STKaiti",serif';
      ctx.fillText('生', s.gx * ts + ts / 2 - 5, s.gy * ts - 2);
      ctx.restore();
    }

    // 器物框
    if (this.mode === 'prop' || this.mode === 'erase') {
      ctx.save();
      ctx.strokeStyle = 'rgba(107,100,92,0.55)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      for (const p of scene.props) ctx.strokeRect(p.gx * ts + 1, p.gy * ts + 1, p.w * ts - 2, p.h * ts - 2);
      ctx.restore();
    }

    // 光标 / 矩形预览
    const draw = (gx, gy, w, h, color) => {
      ctx.save();
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.28;
      ctx.fillRect(gx * ts, gy * ts, w * ts, h * ts);
      ctx.globalAlpha = 1;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.strokeRect(gx * ts + 1, gy * ts + 1, w * ts - 2, h * ts - 2);
      ctx.restore();
    };

    if (this.mode === 'tile' && this.tool === 'rect' && this.rectStart && this.rectEnd) {
      const x0 = Math.min(this.rectStart.gx, this.rectEnd.gx);
      const y0 = Math.min(this.rectStart.gy, this.rectEnd.gy);
      const x1 = Math.max(this.rectStart.gx, this.rectEnd.gx);
      const y1 = Math.max(this.rectStart.gy, this.rectEnd.gy);
      draw(x0, y0, x1 - x0 + 1, y1 - y0 + 1, '#4b8f6a');
    }

    if (this.hover) {
      const g = this.hover;
      if (this.mode === 'prop' && this.propType) {
        const def = getProp(this.propType);
        draw(g.gx, g.gy, def.w || 1, def.h || 1, '#c9a227');
      } else if (this.mode !== 'prop') {
        draw(g.gx, g.gy, 1, 1, this.mode === 'erase' ? '#c8564b' : '#6d8fa8');
      }
      ctx.save();
      ctx.fillStyle = '#3b3733';
      ctx.font = '11px Consolas, Menlo, monospace';
      ctx.fillText(`${g.gx},${g.gy}`, g.gx * ts + 2, g.gy * ts - 3);
      ctx.restore();
    }
  }
}
